
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import { toast } from "sonner";
import type { PerformanceData, Statistics, SubjectMasteryData } from './types';
import AnalyticsHeader from './AnalyticsHeader';
import { StatisticsCards } from './StatisticsCards';
import SubjectPerformance from './SubjectPerformance';
import { TimePerformance } from './TimePerformance';
import { SpeedAccuracyChart } from './SpeedAccuracyChart';
import { PerformanceTrendChart } from './PerformanceTrendChart';
import { DifficultyAnalysis } from './DifficultyAnalysis';
import { SubjectComparisonChart } from './SubjectComparisonChart';
import { TimeAnalysisChart } from './TimeAnalysisChart';
import { TopicHeatmapChart } from './TopicHeatmapChart';
import { PredictiveAnalysisChart } from './PredictiveAnalysisChart';
import SmartRecommendationChart from './SmartRecommendationChart';
import LeaderboardChart from './LeaderboardChart';
import { RetentionAnalysis } from './RetentionAnalysis';
import KnowledgeMasteryHeatmap from './KnowledgeMasteryHeatmap';
import CognitiveLoadIndex from './CognitiveLoadIndex';
import { LearningVelocityChart } from './LearningVelocityChart';
import { ErrorPatternMining } from './ErrorPatternMining';
import { ComparativePeerAnalytics } from './ComparativePeerAnalytics';
import { StrengthWeaknessAnalysis } from './StrengthWeaknessAnalysis';
import { QuizFunnelAnalysis } from './QuizFunnelAnalysis';
import { AIInsightsCards } from './AIInsightsCards';
import { Button } from '@/components/ui/button';

const transformToPerformanceData = (data: any): PerformanceData => {
  console.log('Processing data item:', data);
  return {
    id: data.id,
    score: typeof data.score === 'number' ? data.score : 0,
    subject: data.subject || '',
    category: data.category || '',
    difficulty: data.difficulty || 'easy',
    correct_answers: typeof data.correct_answers === 'number' ? data.correct_answers : 0,
    total_questions: typeof data.total_questions === 'number' ? data.total_questions : 0,
    date: data.date || data.created_at,
    created_at: data.created_at,
    user_id: data.user_id
  };
};

const filterByAttemptedSubjects = (data: PerformanceData[]): PerformanceData[] => {
  const attemptedSubjects = new Set(
    data.filter(item => item.subject && item.total_questions > 0)
        .map(item => item.subject)
  );
  return data.filter(item => attemptedSubjects.has(item.subject));
};

const PerformanceDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  const fetchPerformanceHistory = async () => {
    try {
      console.log('=== CRITICAL FETCH: Starting performance history fetch ===');
      
      // First check authentication status
      const { data: authData, error: authError } = await supabase.auth.getSession();
      if (authError) {
        console.error('Authentication error:', authError);
        throw new Error(`Authentication error: ${authError.message}`);
      }
      
      if (!authData?.session?.user) {
        console.error('No authenticated user found');
        throw new Error('No authenticated user found');
      }
      
      const userId = authData.session.user.id;
      console.log('Authenticated as user ID:', userId);

      // Fetch performance history for the authenticated user
      console.log('Fetching performance history for user:', userId);
      const { data, error } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching performance history:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw error;
      }

      console.log(`Fetched ${data?.length || 0} performance records`);
      
      // Log each record for debugging
      data?.forEach((record, index) => {
        console.log(`Performance record ${index + 1}:`, {
          id: record.id,
          subject: record.subject,
          score: record.score,
          correct_answers: record.correct_answers,
          total_questions: record.total_questions,
          created_at: record.created_at
        });
      });

      // Also fetch performance predictions
      const { data: predictionsData, error: predictionsError } = await supabase
        .from('performance_predictions')
        .select('*')
        .eq('user_id', userId);
        
      if (!predictionsError && predictionsData) {
        console.log(`Fetched ${predictionsData.length} performance predictions`);
      }

      return data || [];
    } catch (err) {
      console.error('Error in performance history fetch:', err);
      throw err;
    }
  };

  const { data: rawPerformanceData, isLoading, error } = useQuery({
    queryKey: ['performance_history'],
    queryFn: fetchPerformanceHistory,
    retry: 3,
    staleTime: 60 * 1000, // 1 minute
  });

  const performanceData = React.useMemo(() => {
    if (!rawPerformanceData) return [];
    console.log(`Processing ${rawPerformanceData.length} performance records`);
    return filterByAttemptedSubjects(rawPerformanceData.map(transformToPerformanceData));
  }, [rawPerformanceData]);

  React.useEffect(() => {
    const performanceChannel = supabase
      .channel('performance_history_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'performance_history'
        },
        (payload) => {
          console.log('Performance data changed:', payload);
          queryClient.invalidateQueries({ queryKey: ['performance_history'] });
          toast.info('Performance data updated!');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(performanceChannel);
    };
  }, [queryClient]);

  const statistics = React.useMemo(() => {
    if (!performanceData?.length) {
      return {
        totalQuizzes: 0,
        averageScore: 0,
        bestScore: 0,
        improving: false
      } as Statistics;
    }

    const totalQuizzes = performanceData.length;
    const averageScore = performanceData.reduce((acc, curr) => acc + (curr.score || 0), 0) / totalQuizzes;
    const bestScore = Math.max(...performanceData.map(d => d.score || 0));
    const recentTrend = performanceData.slice(-5).map(d => d.score || 0);
    const improving = recentTrend[recentTrend.length - 1] > recentTrend[0];

    return { totalQuizzes, averageScore, bestScore, improving } as Statistics;
  }, [performanceData]);

  const subjectAnalysis = React.useMemo(() => {
    if (!performanceData?.length) {
      return {} as Record<string, SubjectMasteryData>;
    }

    return performanceData.reduce((acc, curr) => {
      if (!curr.subject) return acc;
      
      if (!acc[curr.subject]) {
        acc[curr.subject] = {
          attempts: 0,
          totalScore: 0,
          highScore: 0,
          recentScores: []
        };
      }

      acc[curr.subject].attempts++;
      acc[curr.subject].totalScore += curr.score || 0;
      acc[curr.subject].highScore = Math.max(acc[curr.subject].highScore, curr.score || 0);
      acc[curr.subject].recentScores.push(curr.score || 0);

      if (acc[curr.subject].recentScores.length > 5) {
        acc[curr.subject].recentScores = acc[curr.subject].recentScores.slice(-5);
      }

      return acc;
    }, {} as Record<string, SubjectMasteryData>);
  }, [performanceData]);

  const [selectedSubject, setSelectedSubject] = React.useState<string>('');

  React.useEffect(() => {
    if (performanceData?.length > 0) {
      setSelectedSubject(performanceData[performanceData.length - 1].subject);
    }
  }, [performanceData]);

  const checkDatabaseState = async () => {
    try {
      console.log('=== CRITICAL DEBUG: Manual database check ===');
      
      const { data: authData } = await supabase.auth.getSession();
      if (!authData?.session?.user) {
        console.error('No authenticated user found');
        toast.error('Authentication required. Please log in.');
        return;
      }
      
      const userId = authData.session.user.id;
      console.log('Authenticated as user ID:', userId);
      
      // First check existing records
      const { data: existingData, error: existingError } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);
        
      if (existingError) {
        console.error('Error checking existing records:', existingError);
      } else {
        console.log(`Found ${existingData?.length || 0} recent performance records`);
      }
      
      // Now try to insert a test record using the RPC function
      const { data, error } = await supabase.rpc(
        'insert_performance_history',
        {
          p_score: 85,
          p_subject: 'Test Subject',
          p_category: 'Test Category',
          p_difficulty: 'medium',
          p_user_id: userId,
          p_total_questions: 10,
          p_correct_answers: 8
        }
      );
      
      if (error) {
        console.error('Database error:', error);
        toast.error(`Database error: ${error.message}`);
        
        // Try fallback direct insert
        console.log('Attempting fallback direct insert...');
        const { error: directError } = await supabase
          .from('performance_history')
          .insert({
            score: 85,
            subject: 'Test Subject',
            category: 'Test Category',
            difficulty: 'medium',
            user_id: userId,
            total_questions: 10,
            correct_answers: 8,
            created_at: new Date(),
            date: new Date()
          });
          
        if (directError) {
          console.error('Fallback insert error:', directError);
          toast.error(`Fallback insert error: ${directError.message}`);
        } else {
          toast.success('Test record inserted successfully via direct insert!');
          queryClient.invalidateQueries({ queryKey: ['performance_history'] });
        }
        
        return;
      }
      
      // Also update performance predictions
      const { error: predictionError } = await supabase
        .from('performance_predictions')
        .insert({
          user_id: userId,
          subject: 'Test Subject',
          predicted_score: 85.0,
          confidence_level: 0.8,
          recommendations: ['Continue practice', 'Review weak areas']
        });
        
      if (predictionError) {
        console.error('Error inserting prediction:', predictionError);
      } else {
        console.log('Test prediction inserted successfully');
      }
      
      toast.success('Test record inserted successfully!');
      queryClient.invalidateQueries({ queryKey: ['performance_history'] });
    } catch (err) {
      console.error('Error checking database state:', err);
      toast.error('Error checking database state');
    }
  };

  const testAuthenticationStatus = async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Auth error:', error);
        toast.error(`Authentication error: ${error.message}`);
        return;
      }
      
      if (data?.user) {
        console.log('User authenticated:', data.user);
        toast.success(`Authenticated as ${data.user.email}`);
      } else {
        console.error('No user found in auth response');
        toast.error('Not authenticated');
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      toast.error('Error checking authentication');
    }
  };

  if (error) {
    console.error('Performance Dashboard Error:', error);
    return (
      <div className="p-6 border border-red-300 rounded bg-red-50">
        <p className="text-red-600 text-lg font-medium mb-4">Error loading performance data</p>
        <p className="text-red-600 mb-4">{String(error)}</p>
        <Button 
          onClick={() => queryClient.invalidateQueries({ queryKey: ['performance_history'] })}
          className="bg-purple-500 hover:bg-purple-600"
        >
          Retry Data Fetch
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!performanceData?.length) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">No Performance Data Yet</h2>
        <p className="text-gray-600 mb-6">
          Complete some quizzes to see your performance analytics here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto px-4">
      <AnalyticsHeader />
      
      <StatisticsCards statistics={statistics} />
      
      <AIInsightsCards performanceData={performanceData} />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PerformanceTrendChart performanceData={performanceData} />
        <SmartRecommendationChart performanceData={performanceData} />
      </div>
      
      <QuizFunnelAnalysis performanceData={performanceData} />

      <KnowledgeMasteryHeatmap performanceData={performanceData} />
      
      <CognitiveLoadIndex performanceData={performanceData} />
      
      <RetentionAnalysis performanceData={performanceData} />
      
      <ErrorPatternMining performanceData={performanceData} />
      
      <ComparativePeerAnalytics performanceData={performanceData} />

      <LearningVelocityChart performanceData={performanceData} />

      {selectedSubject && (
        <LeaderboardChart 
          subject={selectedSubject}
          performanceData={performanceData}
        />
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DifficultyAnalysis performanceData={performanceData} />
        <SpeedAccuracyChart performanceData={performanceData} />
      </div>
      
      <StrengthWeaknessAnalysis performanceData={performanceData} />
      
      <SubjectComparisonChart performanceData={performanceData} />
      
      <TimeAnalysisChart performanceData={performanceData} />
      
      <TopicHeatmapChart performanceData={performanceData} />
      
      <PredictiveAnalysisChart performanceData={performanceData} />
      
      <SubjectPerformance 
        subjectAnalysis={subjectAnalysis}
        onSubjectSelect={setSelectedSubject}
      />
      
      <TimePerformance performanceData={performanceData} />
    </div>
  );
};

export default PerformanceDashboard;
